import { useEffect, useRef, useState, type ReactNode } from "react";
import { IconButton } from "./Button";

const focusableSelector = [
  "[autofocus]",
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeLabel = "Close",
  closeDisabled = false,
  className = ""
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  closeDisabled?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);
  openRef.current = open;
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      const activeElement = document.activeElement;
      returnFocusRef.current = activeElement instanceof HTMLElement ? activeElement : null;
      dialog.showModal();
      queueMicrotask(() => {
        if (!dialog.open) return;
        const preferred = dialog.querySelector<HTMLElement>("[autofocus]");
        const firstFocusable = dialog.querySelector<HTMLElement>(focusableSelector);
        (preferred ?? firstFocusable ?? dialog).focus();
      });
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const restoreFocus = () => {
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    if (target?.isConnected) {
      queueMicrotask(() => target.focus());
    }
  };

  return (
    <dialog
      ref={ref}
      tabIndex={-1}
      className={`nx-dialog ${className}`.trim()}
      onCancel={(event) => {
        event.preventDefault();
        if (!closeDisabled) onCloseRef.current();
      }}
      onClose={() => {
        restoreFocus();
        if (openRef.current) onCloseRef.current();
      }}
    >
      <header className="nx-dialog__header">
        <div>
          <h2>{title}</h2>
          {description ? <p className="nx-card__description">{description}</p> : null}
        </div>
        <IconButton
          icon="close"
          label={closeLabel}
          variant="ghost"
          size="sm"
          disabled={closeDisabled}
          onClick={onClose}
        />
      </header>
      <div className="nx-dialog__body">{children}</div>
      {footer ? <footer className="nx-dialog__footer">{footer}</footer> : null}
    </dialog>
  );
}

export function Drawer(props: Parameters<typeof Dialog>[0]) {
  return <Dialog {...props} className={`nx-drawer ${props.className ?? ""}`.trim()} />;
}

export type CommandItem = {
  id: string;
  label: string;
  description?: string;
  shortcut?: string;
  onSelect: () => void;
};

export function CommandPalette({
  open,
  onClose,
  items,
  placeholder = "Search commands"
}: {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const filtered = items.filter((item) =>
    `${item.label} ${item.description ?? ""}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} title="Command palette" className="nx-command">
      <input
        autoFocus
        className="nx-command__search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      <div className="nx-command__list">
        {filtered.map((item) => (
          <button
            key={item.id}
            className="nx-command__item"
            type="button"
            onClick={() => {
              item.onSelect();
              onClose();
            }}
          >
            <span>
              <strong>{item.label}</strong>
              {item.description ? (
                <small className="nx-field__hint" style={{ display: "block" }}>
                  {item.description}
                </small>
              ) : null}
            </span>
            {item.shortcut ? <span className="nx-command__shortcut">{item.shortcut}</span> : null}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
